/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { getApps } from '@/api/apps';
import { ApplicationsContext } from '@/context';
import { AppList, AppListItem } from 'types/app-list';

type ApplicationsProviderProps = {
  children: ReactNode;
};

export type ApplicationsContextValue = {
  apps: AppList;
  customApplications: AppList;
  filteredCustomApplications: AppList;
  filteredAppNames: string[];
  selectedApps: string[];
  setSelectedApps: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  error: unknown;
  refreshApplications: () => Promise<void>;
};

export const ApplicationsProvider = ({
  children,
}: ApplicationsProviderProps) => {
  const [apps, setApps] = useState<AppList>([]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  /**
   * Fetches the list of applications and updates state accordingly. Handles loading and error states.
   */
  const refreshApplications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getApps();
      setApps(data);
    } catch (fetchError) {
      setError(fetchError);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshApplications();
  }, [refreshApplications]);

  /**
   * Derives custom applications by filtering out playground apps from the full app list.
   */
  const customApplications = useMemo(
    () => apps.filter((app: AppListItem) => !app.is_playground),
    [apps]
  );

  /**
   * Derives filtered custom applications based on selected categories.
   */
  const filteredCustomApplications = useMemo(
    () =>
      customApplications.filter(
        (app: AppListItem) =>
          selectedApps.length === 0 ||
          app.categories?.some((category) => selectedApps.includes(category))
      ),
    [customApplications, selectedApps]
  );

  /**
   * Derives filtered custom application names based on selected categories.
   */
  const filteredAppNames = useMemo(
    () =>
      filteredCustomApplications
        .map((app: AppListItem) => app.name)
        .filter(Boolean),
    [filteredCustomApplications]
  );

  return (
    <ApplicationsContext.Provider
      value={{
        apps,
        customApplications,
        filteredCustomApplications,
        filteredAppNames,
        selectedApps,
        setSelectedApps,
        loading,
        error,
        refreshApplications,
      }}
    >
      {children}
    </ApplicationsContext.Provider>
  );
};
